import { customElement, query } from 'lit/decorators.js';
import { WEB_COMPONENT_UPDATE_SECURITY_SERVER } from '../../../../../webcomponents.constants';
import { html, LitElement, PropertyValues } from 'lit';
import { styles } from './view/styles';
import { FontSizes, LabelWidthValues } from '../../../../../types/common/types';
import { translateFn } from '../../../../../utils/translator.helper';
import { InfoBoxSeverity } from '../../../../../ui/gzInfoBox/types';
import { GzInfoBoxWebComponent } from '../../../../../ui/gzInfoBox';
import { GzDatePickerWebComponent } from '../../../../../ui/gzDatePicker';
import { GzRadioGroupWebComponent } from '../../../../../ui/gzRadio';
import { GzFormFieldWebComponent } from '../../../../../ui/gzForm/formField';
import { UpdateSecurityServerTaskSpecifics } from './types';
import { NetworkPopUpEvents } from '../../NetworkBasePopUp';
import { CustomizableValidator } from '../../../../../ui/gzForm/validators/customizable.validator';
import { ValidationResult } from '../../../../../ui/gzForm/formField/types/form-validator';
import {
  computeMinTimestampForDatePicker,
  getDateElementsFromTimestampString,
} from '../../../helpers/datePicker.utils';
import { ElementClass } from '../../../../../utils/htmlElement.utils';
import { GzDropdownMenuWebComponent } from '../../../../../ui/gzDropdownMenu';

@customElement(WEB_COMPONENT_UPDATE_SECURITY_SERVER)
export class GzUpdateSecurityServerWebComponent extends LitElement {
  static get styles() {
    return styles;
  }

  @query('#update-info-box')
  updateInfoBox: GzInfoBoxWebComponent;

  @query('#update-type-radio-form-field')
  updateTypeFormField: GzFormFieldWebComponent<GzRadioGroupWebComponent>;

  @query('#date-time-radio-form-field')
  updateTimeFormField: GzFormFieldWebComponent<GzRadioGroupWebComponent>;

  @query('#update-datetime-picker')
  dateTimePicker: GzDatePickerWebComponent;

  @query('#date-time-section')
  dateTimeFormContainer: HTMLDivElement;

  protected async firstUpdated(_changedProperties: PropertyValues) {
    super.firstUpdated(_changedProperties);
    await this.updateComplete;
    this.setTranslatedStrings();
    this.updateTypeFormField.Input.setFormValue(Constants.Tasks.Endpoints.UPDATE_SVA_TYPE.SYSTEM.toString());
    this.updateTimeFormField.Validators = [CustomizableValidator(this.validateDateForm.bind(this))];
    this.updateTimeFormField.valid$.subscribe(valid => {
      this.setEnabledConfirmationButton(valid);
    });

    const dropdownMenu = this.dateTimePicker.shadowRoot?.querySelector(
      'gz-dropdown-menu'
    ) as GzDropdownMenuWebComponent;
    dropdownMenu.addEventListener('close', this.handleDateSelected.bind(this));
  }

  private setTranslatedStrings() {
    const kbLink = Application.Main.company.isPartner()
      ? 'https://www.bitdefender.com/business/support/en/77211-113596-update-the-operating-system-of-the-security-server-to-ubuntu-20-04-lts.html'
      : 'https://www.bitdefender.com/business/support/en/77209-113596-update-the-operating-system-of-the-security-server-to-ubuntu-20-04-lts.html';

    this.updateInfoBox.innerHTML = translateFn(
      'During the OS upgrade, the Security Server will be unavailable for scanning. This means connected endpoints may be left unprotected if they rely solely on Central Scan without a fallback or do not have an alternative Security Server configured. Do not update the operating system on all Security Servers simultaneously.{OPEN_TAG} Learn more {CLOSE_TAG}',
      {
        OPEN_TAG: `<gz-link href="${kbLink}">`,
        CLOSE_TAG: `</gz-link>`,
      }
    );
  }

  private handleDateSelected(event: CustomEvent) {
    event.stopImmediatePropagation();
    this.updateTimeFormField.validate(true);
  }

  private validateDateForm(value): ValidationResult {
    const parsedValue = parseInt(value, 10);

    if (parsedValue === Constants.Tasks.Endpoints.UPDATE_SVA_TIME.NOW || parsedValue === -1) {
      return {
        valid: true,
      };
    }

    const selectedTimestamp = this.dateTimePicker.selectedTimestamp;

    if (selectedTimestamp === undefined) {
      return {
        valid: false,
        error: translateFn('Select a valid time and date.'),
      };
    }

    const currentTime = Math.floor(Date.now() / 1000);
    if (currentTime - selectedTimestamp >= 60) {
      return {
        valid: false,
        error: translateFn('Select a valid time and date.'),
      };
    }

    return {
      valid: true,
    };
  }

  private handleUpdateTypeChange(event: CustomEvent) {
    if (event.detail.selectedValue === Constants.Tasks.Endpoints.UPDATE_SVA_TYPE.OS.toString()) {
      this.updateInfoBox.classList.remove(ElementClass.HIDDEN);
      this.dateTimeFormContainer.classList.remove('disabled');
      this.updateTimeFormField.Input.setEnabled(true);
      this.updateTimeFormField.Input.setFormValue(Constants.Tasks.Endpoints.UPDATE_SVA_TIME.NOW.toString());
    } else {
      this.updateInfoBox.classList.add(ElementClass.HIDDEN);
      this.updateTimeFormField.Input.setFormValue(-1);
      this.updateTimeFormField.Input.setEnabled(false);
      this.dateTimePicker.setEnabled(false);
      this.dateTimeFormContainer.classList.add('disabled');
    }
  }

  private handleUpdateTimeChanged(event: CustomEvent) {
    if (event.detail.selectedValue === Constants.Tasks.Endpoints.UPDATE_SVA_TIME.ON.toString()) {
      this.dateTimePicker.setEnabled(true);
    } else {
      this.dateTimePicker.setEnabled(false);
    }
  }

  private setEnabledConfirmationButton(enabled: boolean) {
    this.dispatchEvent(
      new CustomEvent(NetworkPopUpEvents.SET_CONFIRM_BUTTON, {
        bubbles: true,
        composed: true,
        detail: { value: !enabled },
      })
    );
  }

  private getDateElementsFromTimestamp(timestamp: number): {
    updateOnDate: string;
    updateOnHour: number;
    updateOnMinute: number;
  } {
    const { fullDate, hour, minute } = getDateElementsFromTimestampString(timestamp);

    return {
      updateOnDate: fullDate,
      updateOnHour: hour,
      updateOnMinute: minute,
    };
  }

  public getRequestTaskSpecifics(): UpdateSecurityServerTaskSpecifics {
    const updateSvaType = parseInt(this.updateTypeFormField.value as string, 10);
    const updateSvaTime = parseInt(this.updateTimeFormField.value as string, 10);

    let specifics: UpdateSecurityServerTaskSpecifics = {
      updateSvaType,
    };

    if (updateSvaTime > 0) {
      specifics.updateSvaTime = updateSvaTime;

      if (updateSvaTime === Constants.Tasks.Endpoints.UPDATE_SVA_TIME.ON) {
        const { updateOnDate, updateOnHour, updateOnMinute } = this.getDateElementsFromTimestamp(
          this.dateTimePicker.selectedTimestamp * 1000
        );
        specifics = {
          ...specifics,
          updateOnDate,
          updateOnHour,
          updateOnMinute,
        };
      }
    }

    return specifics;
  }

  protected render() {
    return html`
      <div class="container">
        <gz-info-box id="update-info-box" class="update-info-box" severity="${InfoBoxSeverity.WARNING}"> </gz-info-box>
        <gz-text fontSize="${FontSizes.HEADING_6}" class="field-container">
          ${translateFn('Select the update type for the target Security Servers.')}
        </gz-text>
        <gz-form-field id="update-type-radio-form-field">
          <gz-radio-group
            gz-form-input
            class="d-flex flex-column"
            @radioSelectionChanged="${this.handleUpdateTypeChange}"
          >
            <gz-radio
              class="field-container"
              value=${Constants.Tasks.Endpoints.UPDATE_SVA_TYPE.SYSTEM}
              label="${translateFn('Security features')}"
            ></gz-radio>
            <gz-radio
              class="field-container"
              value=${Constants.Tasks.Endpoints.UPDATE_SVA_TYPE.OS}
              label="${translateFn('Operating system')}"
            ></gz-radio>
          </gz-radio-group>
        </gz-form-field>

        <div id="date-time-section" class="disabled pl-4">
          <gz-text fontSize="${FontSizes.HEADING_6}" class="field-container">
            ${translateFn('Select the time and date when the update should run:')}
          </gz-text>
          <gz-form-field id="date-time-radio-form-field" disabled>
            <gz-radio-group
              gz-form-input
              class="d-flex flex-column"
              @radioSelectionChanged="${this.handleUpdateTimeChanged}"
            >
              <gz-radio
                class="field-container"
                value=${Constants.Tasks.Endpoints.UPDATE_SVA_TIME.NOW}
                label="${translateFn('Now')}"
              ></gz-radio>
              <div class="field-container schedule-field">
                <gz-radio
                  value=${Constants.Tasks.Endpoints.UPDATE_SVA_TIME.ON}
                  label="${translateFn('Schedule on: ')}"
                ></gz-radio>
                <gz-date-picker
                  id="update-datetime-picker"
                  disabled
                  class="ml-5"
                  gz-form-input
                  placeholder="${translateFn('Time and date')}"
                  hasTimePicker
                  labelWidth="${LabelWidthValues.LARGE}"
                  .pickerStrings="${{
                    confirmActionButton: translateFn('Apply'),
                    cancelActionButton: translateFn('Cancel'),
                    currentDay: translateFn('This month'),
                  }}"
                  min=${computeMinTimestampForDatePicker()}
                  userLocal="${Application.Main.getUser().profile.language}"
                  timezone="${Application.Main.getUser().profile.timezone}"
                  @dateIsSelected="${this.handleDateSelected}"
                ></gz-date-picker>
              </div>
            </gz-radio-group>
            <gz-input-error></gz-input-error>
          </gz-form-field>
        </div>
      </div>
    `;
  }
}
